import React, { useState, useEffect } from 'react';
import ComputerPlayer from './ComputerPlayer.js';
import { GreenCircle, YellowCircle, RedCircle } from './Icons.js';

// Traffic Lights Game Component
const TrafficLights = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [playerTypes, setPlayerTypes] = useState({ 1: 'human', 2: 'human' });
  const [board, setBoard] = useState(Array(12).fill(null)); // 3 rows x 4 columns = 12 cells
  const [currentPlayer, setCurrentPlayer] = useState(1); // Player 1 or Player 2
  const [winner, setWinner] = useState(null);
  const [computerPlayer] = useState(new ComputerPlayer());

  // Calculate winner - check for 3 in a row of the same color
  const calculateWinner = (squares, player) => {
    const lines = [
      // Horizontal lines
      [0, 1, 2], [1, 2, 3],
      [4, 5, 6], [5, 6, 7],
      [8, 9, 10], [9, 10, 11],
      // Vertical lines
      [0, 4, 8], [1, 5, 9], [2, 6, 10], [3, 7, 11],
      // Diagonal lines (down-right)
      [0, 5, 10], [1, 6, 11],
      // Diagonal lines (down-left)
      [2, 5, 8], [3, 6, 9],
    ];

    for (const [a, b, c] of lines) {
      if (squares[a] && squares[b] && squares[c] &&
          squares[a].color === squares[b].color &&
          squares[a].color === squares[c].color) {
        return { player: player, color: squares[a].color };
      }
    }
    return null;
  };

  // Handle cell click with automatic color progression
  const handleClick = (index) => {
    if (winner) {
      return;
    }

    const cell = board[index];
    let nextColor;
    
    // Determine next color based on current cell state
    if (cell === null) {
      // Empty cell -> place green
      nextColor = 'green';
    } else if (cell.color === 'green') {
      // Green -> change to yellow
      nextColor = 'yellow';
    } else if (cell.color === 'yellow') {
      // Yellow -> change to red
      nextColor = 'red';
    } else {
      // Red -> cannot be clicked (no further progression)
      return;
    }

    // Update board with new piece
    const newBoard = [...board];
    newBoard[index] = { player: currentPlayer, color: nextColor };
    setBoard(newBoard);

    // Check for winner
    const gameWinner = calculateWinner(newBoard, currentPlayer);
    if (gameWinner) {
      setWinner(gameWinner);
    } else {
      // Automatically switch to next player
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    }
  };



  // Reset game
  const resetGame = () => {
    setBoard(Array(12).fill(null));
    setCurrentPlayer(1);
    setWinner(null);
    setGameStarted(false);
  };

  // Start game with selected player types
  const startGame = () => {
    setGameStarted(true);
  };

  // Handle player type selection
  const handlePlayerTypeChange = (player, type) => {
    setPlayerTypes(prev => ({ ...prev, [player]: type }));
  };

  // Handle computer player moves
  useEffect(() => {
    if (!gameStarted || winner) return;

    const isComputerTurn = playerTypes[currentPlayer] === 'computer';

    if (isComputerTurn) {
      // Trigger computer move after a short delay for better UX
      const timeoutId = setTimeout(() => {
        const move = computerPlayer.makeMove(board, currentPlayer);
        if (move !== null) {
          // Computer player would make move here
          // For now, since logic is empty, we do nothing
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, gameStarted, winner, board, playerTypes]);

  // Render cell content
  const renderCell = (index) => {
    const cell = board[index];
    if (!cell) return null;
    
    if (cell.color === 'green') {
      return <GreenCircle />;
    } else if (cell.color === 'yellow') {
      return <YellowCircle />;
    } else if (cell.color === 'red') {
      return <RedCircle />;
    }
    return null;
  };

  // Get game status message
  const getStatus = () => {
    if (winner) {
      return `Vencedor: Jogador ${winner.player}`;
    } else {
      return `Turno do Jogador ${currentPlayer}`;
    }
  };

  // Show player configuration screen before game starts
  if (!gameStarted) {
    return (
      <div className="game-container">
        <h1 className="game-title">Semáforo</h1>
        <h2 className="config-subtitle">Configuração dos Jogadores</h2>
        <div className="player-config">
          <div className="player-config-item">
            <h3>Jogador 1</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes[1] === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(1, 'human')}
              >
                Humano
              </button>
              <button
                className={`player-type-button ${playerTypes[1] === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(1, 'computer')}
              >
                Computador
              </button>
            </div>
          </div>
          <div className="player-config-item">
            <h3>Jogador 2</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes[2] === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(2, 'human')}
              >
                Humano
              </button>
              <button
                className={`player-type-button ${playerTypes[2] === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(2, 'computer')}
              >
                Computador
              </button>
            </div>
          </div>
        </div>
        <button className="new-game-button" onClick={startGame}>
          Iniciar Jogo
        </button>
      </div>
    );
  }

  return (
    <div className="game-container">
      <h1 className="game-title">Semáforo</h1>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      <div className="game-board traffic-lights-board">
        {board.map((cell, index) => (
          <button
            key={index}
            className="cell"
            onClick={() => handleClick(index)}
            disabled={winner !== null}
          >
            {renderCell(index)}
          </button>
        ))}
      </div>
      <div className="game-buttons">
        <button className="new-game-button" onClick={resetGame}>
          Novo Jogo
        </button>
      </div>
    </div>
  );
};

export default TrafficLights;
